/**
 * Admin Session Show — the read-only destination for a session reference
 * (COP-134 D1 / ATRU-04).
 *
 * A scheduled run writes a real chat session; task-04 put that `sessionId` on
 * the run-history row, but a reference you cannot open is still a dead end.
 * This page is the destination, reached ONLY by deep link with an id the caller
 * already legitimately holds (their own run history). There is deliberately NO
 * session list, search or user picker here — no cross-owner browser is
 * introduced (HUB-024, COP-074/COP-075).
 *
 * AUTHZ. `GET /sessions/:id/messages` guards
 * `session#read = owner ∪ parent.track#admin` via `fastify.authz.guard` BEFORE
 * any read, and answers 403 otherwise — so this page needs no `?userId=`
 * scoping param (unlike `approvals/Show.tsx`, whose ADMIN route scopes the read
 * under `withUserRole(targetUserId)` per HUB-024 §6). The route is therefore
 * auth-gated only and NOT under `RequireAdmin`; see the block comment in
 * `App.tsx`.
 *
 * Rendering lives in `session-transcript.tsx` so the unit tests can render it
 * without importing `@/lib/hub-client` (which boots a HubClient at load).
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import { hub } from '@/lib/hub-client';
import {
  SessionViewer,
  type SessionMessagesData,
  type SessionViewerState,
} from './session-transcript';

export function SessionsShow() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<SessionViewerState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setState({ kind: 'error', httpStatus: 404, message: 'Missing session id in URL' });
      return () => {
        cancelled = true;
      };
    }
    setState({ kind: 'loading' });
    hub
      .sessionsMessages({ params: { id } })
      // Annotated, not cast: the endpoint's typed response must stay assignable
      // to the view's data shape, so a schema change surfaces here.
      .then((res: SessionMessagesData) => {
        if (!cancelled) setState({ kind: 'ready', data: res });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          httpStatus: err instanceof HubClientError ? err.httpStatus : null,
          message: (err as Error).message ?? 'Failed to load session',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  return <SessionViewer state={state} />;
}
